import { redirect } from '@tanstack/react-router';

import { can, type Capability, type CurrentUser } from '../lib/capabilities';

// Router context shape established by createRootRouteWithContext in AppRouter.tsx.
// The signed-in user is threaded into the router context so route guards can
// read it without reaching into React context.
export interface RouterContext {
  auth: CurrentUser | undefined;
}

// Call sites pass route-valid redirect targets, but TanStack's redirect type is
// tied to the concrete router registration and rejects dynamic params from this
// shared helper. Keep the public input flexible and cast at the redirect
// boundary.
type RedirectTarget = Record<string, unknown>;
type RedirectArgs = Parameters<typeof redirect>[0];

/**
 * A `beforeLoad` guard that redirects users lacking `capability` away from a
 * write-only route, before its component (and any data loader) runs.
 *
 * This is the single home for read-only redirect logic — every write-only route
 * reuses it instead of repeating a `useEffect(() => navigate(...))` in its
 * component. The server policy still enforces access (403 / redirect); this is
 * the UX side, keeping a read-only user out of an editable surface they could
 * otherwise reach via a direct URL or stale link.
 *
 * Redirects client-side (replace: true) so SPA state is kept and the forbidden
 * URL stays out of history — no full page reload.
 */
export function requireCapability(
  capability: Capability,
  target: RedirectTarget | ((args: { params: Record<string, string> }) => RedirectTarget),
) {
  return ({
    context,
    params,
  }: {
    context: RouterContext;
    params: Record<string, string>;
  }): void => {
    if (can(context.auth, capability)) return;
    const opts = typeof target === 'function' ? target({ params }) : target;
    throw redirect({ replace: true, ...opts } as RedirectArgs);
  };
}
